import { ImageServiceType } from '@omni/kit/Types';
import { parseImageUrl } from '@omni/kit/utilities/utilities';
import React, { ReactElement, useRef, useState } from 'react';
import {
  Platform,
  StyleSheet,
  TouchableWithoutFeedback,
  View,
} from 'react-native';

import { PlatformAgnosticFile } from '../../FileTypes';
import { useImageSelector } from '../../hooks/useImageSelector';
import useImageUpload from '../../hooks/useImageUpload';
import Colors from '../../theming/Colors';
import Spacing from '../../theming/Spacing';
import KitActionSheetItem from '../KitActionSheetItem';
import KitImage from '../KitImage';
import { KitSnackDuration } from '../KitSnack.types';
import Show from '../Show';
import {
  KitIcon,
  KitLoader,
  KitModal,
  KitSnack,
  KitText,
  KitTouchable,
} from '../index';

const debug = require('debug')('tca:component:PhotoUpload');

export interface PhotoUploadProps {
  appKey: string;
  endUserId: string;
  nickname: string;
  imageUrl?: string;
  onPhotoChange?: any;
}

export default function PhotoUpload({
  appKey,
  endUserId,
  nickname = '',
  imageUrl = '',
  onPhotoChange,
}: PhotoUploadProps): ReactElement {
  const { uploadImage } = useImageUpload(appKey, endUserId);
  const {
    _onChangePhotoPressWeb,
    _onChangePhotoPressMobile,
    _onCameraPress,
    _onFilePress,
  } = useImageSelector();

  const firstNameChar = nickname.charAt(0).toUpperCase();
  const lastNameChar = nickname.split(' ')[1]?.charAt(0).toUpperCase() || '';

  const [modalVisible, setModalVisible] = useState(false);
  const [profileImage, setProfileImage] = useState<string | undefined>(
    imageUrl
  );
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const _renderInitials = () => {
    return (
      <KitText
        fontSize={36}
        white
        center
        style={styles.initialText}
        testID='Initials'
      >
        {String(firstNameChar) + lastNameChar}
      </KitText>
    );
  };

  const _openWebFilePicker = () => {
    fileInputRef.current?.click();
  };

  const _uploadPhoto = (selectedFile: PlatformAgnosticFile) => {
    try {
      setUploading(true);
      setModalVisible(false);
      uploadImage(selectedFile, 'profile')
        .then(({ id, url }) => {
          setUploading(false);
          const imageUrl = parseImageUrl(
            url,
            100,
            100,
            ImageServiceType.ImageJpeg
          );
          setProfileImage(imageUrl);
          onPhotoChange?.({ id, url: imageUrl });
        })
        .catch(() => {
          debug('Error Uploading Photo');
          setUploading(false);
          KitSnack.show(
            'Unable to upload photo. Please try again.',
            KitSnackDuration.SHORT
          );
        });
    } catch (e) {
      debug('Error Uploading Photo');
      setUploading(false);
    }
  };
  const handleOnChangeWeb = async (
    e: React.ChangeEvent<HTMLInputElement> | undefined
  ) => {
    _onChangePhotoPressWeb(e)
      .then((r) => r && _uploadPhoto(r))
      .catch((e) => debug(e));
  };

  const handleOnChangeMobile = async () => {
    _onChangePhotoPressMobile(true)
      .then((r) => r && _uploadPhoto(r))
      .catch((e) => debug(e));
  };

  return (
    <>
      <View style={styles.container} testID='KitEdiableAvatar'>
        <View>
          <KitTouchable
            underlayColor={profileImage ? Colors.N1000 : Colors.N300}
            onPress={
              Platform.OS === 'web'
                ? _openWebFilePicker
                : () => setModalVisible(true)
            }
            style={styles.avatarContainer}
          >
            <View style={{ flex: 1 }}>
              <>
                <Show show={Boolean(!uploading)}>
                  <>
                    {imageUrl ? (
                      <KitImage
                        // @ts-ignore
                        style={styles.avatarImage}
                        source={{
                          uri: parseImageUrl(
                            imageUrl,
                            100,
                            100,
                            ImageServiceType.ImageJpeg
                          ),
                        }}
                      />
                    ) : (
                      <View
                        style={{
                          flex: 1,
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {_renderInitials()}
                      </View>
                    )}
                  </>
                </Show>
                <Show show={Boolean(uploading)}>
                  <View
                    style={{
                      flex: 1,
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {uploading ? <KitLoader white /> : null}
                  </View>
                </Show>
              </>
            </View>
          </KitTouchable>
          {/* This does the same as the above KitTouchable, but needs to be outside the "overflow: hidden" property
        so the press state has the correct border radius. */}
          {Platform.OS !== 'web' ? (
            <TouchableWithoutFeedback onPress={() => setModalVisible(true)}>
              <View style={styles.photoIndicator}>
                <KitIcon name='camera' size={13} color={Colors.N900} />
              </View>
            </TouchableWithoutFeedback>
          ) : (
            // @ts-ignore
            <label
              style={{
                ...(photoIndicatorStyles as React.CSSProperties),
                zIndex: 3,
              }}
            >
              <View
                style={{
                  flex: 1,
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                  borderWidth: 3,
                  borderColor: Colors.N0,
                  borderRadius: 15,
                }}
              >
                <KitIcon name='camera' size={14} color={Colors.N900} />
              </View>
              <input
                ref={fileInputRef}
                style={{ display: 'none' }}
                type='file'
                onChange={
                  Platform.OS === 'web'
                    ? handleOnChangeWeb
                    : () => setModalVisible(true)
                }
                accept='image/*'
              />
            </label>
          )}
        </View>
      </View>
      {Platform.OS !== 'web' ? (
        <KitModal
          visible={modalVisible}
          setVisible={setModalVisible}
          testID='SelectImageModal'
        >
          <View style={styles.modalContainer}>
            <KitActionSheetItem
              icon='image-library'
              label='Photo Library'
              onPress={handleOnChangeMobile}
            />
            <KitActionSheetItem
              icon='camera'
              label='Take a photo'
              onPress={() =>
                _onCameraPress()
                  .then((r) => r && _uploadPhoto(r))
                  .catch((e) => debug(e))
              }
            />
            {Platform.OS === 'ios' ? (
              <KitActionSheetItem
                icon='action-h'
                label='Browse'
                onPress={() =>
                  _onFilePress()
                    .then((r) => r && _uploadPhoto(r))
                    .catch((e) => debug(e))
                }
              />
            ) : null}
          </View>
        </KitModal>
      ) : null}
    </>
  );
}

//******************************************************************************
// Styles
//******************************************************************************

const photoIndicatorStyles = {
  alignItems: 'center',
  justifyContent: 'center',
  position: 'absolute',
  width: 30,
  height: 30,
  borderRadius: 15,
  bottom: 0,
  right: -3,
  backgroundColor: Colors.N100,
  borderWidth: 3,
  borderColor: Colors.N0,
  ...Platform.select({ web: { cursor: 'pointer' } }),
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: Spacing.s,
  },
  modalContainer: {
    marginHorizontal: Spacing.l,
    marginTop: Spacing.m,
    paddingBottom: Spacing.xl,
  },
  avatarContainer: {
    width: 104,
    height: 104,
    borderRadius: 50,
    overflow: 'hidden',
    backgroundColor: Colors.N200,
  },
  avatarImage: {
    position: 'absolute',
    width: 104,
    height: 104,
    borderRadius: 50,
    zIndex: 2,
  },
  // @ts-ignore
  photoIndicator: {
    ...photoIndicatorStyles,
  },
  initialText: {
    zIndex: 1,
  },
});
